import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ComboBox, useDialog } from '@gokrew/ui';
import {
  Check,
  Mail,
  Phone,
  CalendarDays,
  CircleCheck,
  MessageSquare,
  ArrowRight,
  Plus,
  Activity,
  Sparkles,
  Pencil,
} from 'lucide-react';
import {
  completeCrmActivity,
  getCrmActivities,
  getCrmMembers,
} from '@/api/crm';
import type { CrmActivity } from '@/api/crm';
import { personName, useCrmFormat } from './crm-format';
import { CrmButton, CrmEmpty, CrmError, CrmPagination } from './CrmShared';
import { crmRowInteraction } from './crm-row-interaction';
import { CrmActivityDetail } from './CrmActivityDetail';
import { CrmActivityEditor } from './CrmActivityEditor';
import { hasCrmAccess } from './crm-access';
import styles from './Crm.module.css';

const activityIcon = (kind: string) =>
  kind.includes('email')
    ? Mail
    : kind === 'call'
      ? Phone
      : kind === 'meeting'
        ? CalendarDays
        : kind.includes('task')
          ? CircleCheck
          : kind.includes('stage')
            ? ArrowRight
            : kind.includes('created')
              ? Plus
              : kind === 'note'
                ? MessageSquare
                : Activity;
const list = (value: unknown) =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
const fromCalendarEntry = (activity: CrmActivity) =>
  (activity.metadata ?? {}).source === 'calendar';
export const CrmActivityFeed = ({
  activities,
  onDeal,
  compact = false,
}: {
  activities: CrmActivity[];
  onDeal?: (id: string) => void;
  compact?: boolean;
}) => {
  const { t, shortDateTime, money, date } = useCrmFormat();
  const { openDialog } = useDialog();
  const openDetail = (activity: CrmActivity) =>
    openDialog({
      title: activity.title,
      containerClass: styles.editorDialog,
      content: <CrmActivityDetail activity={activity} />,
    });
  const openEditor = (activity: CrmActivity) =>
    openDialog({
      title: t('crm.workspace.edit_activity'),
      containerClass: styles.editorDialog,
      content: <CrmActivityEditor activity={activity} />,
    });
  const members = useQuery({
    queryKey: ['crm', 'members'],
    queryFn: getCrmMembers,
    enabled: activities.some((a) => a.kind === 'deal_updated'),
  });
  /** "Employés : 85 → 120" for each field a deal edit changed. */
  const describeChanges = (metadata: Record<string, unknown>) => {
    const value = (key: string, raw: unknown) => {
      if (raw === null || raw === undefined || raw === '') return '—';
      if (key === 'estimatedEmployees') return String(raw);
      if (key === 'atsMrrCents') return money(Number(raw));
      if (key === 'expectedCloseDate') return date(String(raw));
      // A task's due date is a day, no time.
      if (key === 'dueAt' && metadata.activityKind === 'task')
        return date(String(raw));
      if (key === 'dueAt' || key === 'occurredAt')
        return shortDateTime(String(raw));
      if (key === 'product') return t(`crm.products.${String(raw)}`);
      if (key === 'ownerUserId') {
        const member = members.data?.find((m) => m.userId === raw);
        return member ? personName(member) : '—';
      }
      return String(raw);
    };
    const changes =
      metadata.changes && typeof metadata.changes === 'object'
        ? (metadata.changes as Record<string, { from?: unknown; to?: unknown }>)
        : null;
    const keys = changes
      ? Object.keys(changes)
      : Array.isArray(metadata.fields)
        ? metadata.fields.filter((k): k is string => typeof k === 'string')
        : [];
    return keys
      .filter((key) => key !== 'stage')
      .map((key) => {
        const label = t(`crm.workspace.log_fields.${key}`, {
          defaultValue: key,
        });
        const change = changes?.[key];
        return change &&
          'from' in change &&
          !['companyId', 'contactId'].includes(key)
          ? `${label} : ${value(key, change.from)} → ${value(key, change.to)}`
          : label;
      })
      .join(' · ');
  };
  const client = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const complete = async (activity: CrmActivity) => {
    if (!activity.dealId) return;
    setBusy(activity.id);
    setError('');
    try {
      await completeCrmActivity(activity.dealId, activity.id);
      await client.invalidateQueries({ queryKey: ['crm'] });
    } catch {
      setError(t('crm.save_error'));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className={styles.activityFeed}>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      {activities.map((activity) => {
        const Icon = activityIcon(activity.kind);
        const actor =
          [activity.actor?.firstName, activity.actor?.lastName]
            .filter(Boolean)
            .join(' ') ||
          activity.actor?.email ||
          t('crm.workspace.system');
        const metadata = activity.metadata ?? {};
        const from =
          typeof metadata.fromStage === 'string'
            ? metadata.fromStage
            : typeof metadata.from === 'string'
              ? metadata.from
              : null;
        const to =
          typeof metadata.toStage === 'string'
            ? metadata.toStage
            : typeof metadata.to === 'string'
              ? metadata.to
              : null;
        const recipients = Array.isArray(metadata.to)
          ? metadata.to.filter(
              (value): value is string => typeof value === 'string'
            )
          : [];
        const subject =
          typeof metadata.subject === 'string' ? metadata.subject : null;
        const emailFrom =
          typeof metadata.fromEmail === 'string'
            ? metadata.fromEmail
            : typeof metadata.from === 'string' &&
                activity.kind.includes('email')
              ? metadata.from
              : null;
        // Older syncs logged mail from a company mailbox (sales@...) as
        // received; it was sent by us.
        const kind =
          activity.kind === 'email_received' &&
          typeof metadata.from === 'string' &&
          hasCrmAccess(metadata.from)
            ? 'email_sent'
            : activity.kind;
        const kindLabel = t(`crm.workspace.activityKinds.${kind}`, {
          defaultValue: activity.title,
        });
        // Inside a deal, an email opens its content and a calendar call its
        // invite list.
        const detail =
          !onDeal &&
          (activity.kind.startsWith('email_') ||
            (fromCalendarEntry(activity) &&
              list(metadata.attendees).length > 0));
        // System entries (stage changed, deal created...) carry an English
        // title; the translated kind already says it.
        const showTitle =
          (['note', 'task', 'call', 'meeting'].includes(activity.kind) ||
            (!compact && activity.kind.includes('email'))) &&
          !!activity.title &&
          activity.title !== kindLabel;
        const fromCalendar = metadata.source === 'calendar';
        // Written by hand: a note, task, call or meeting; synced calendar
        // entries come from the mailbox.
        const editable =
          !!activity.dealId &&
          !fromCalendar &&
          ['note', 'task', 'call', 'meeting'].includes(activity.kind);
        const open = expanded.has(activity.id);
        return (
          <article
            key={activity.id}
            className={`${styles.activityItem} ${(onDeal && activity.dealId) || detail ? styles.clickableRow : ''}`}
            aria-label={
              onDeal && activity.dealId
                ? t('crm.open_deal_named', {
                    name: activity.deal?.name || activity.title,
                  })
                : undefined
            }
            {...(onDeal && activity.dealId
              ? crmRowInteraction(() => onDeal(activity.dealId!))
              : detail
                ? crmRowInteraction(() => openDetail(activity))
                : {})}
          >
            <span
              className={`${styles.activityIcon} ${activity.completedAt ? styles.completedIcon : ''}`}
            >
              <Icon aria-hidden="true" />
            </span>
            <div className={styles.activityContent}>
              <div className={styles.activityLine}>
                <strong>{kindLabel}</strong>
                {showTitle && (
                  <span className={styles.activityTitle} title={activity.title}>
                    {activity.title}
                  </span>
                )}
                {fromCalendar && (
                  <span className={styles.activitySource}>
                    <CalendarDays />
                    {t('crm.workspace.from_calendar')}
                  </span>
                )}
                {from && to && (
                  <span className={styles.stageChange}>
                    <span>{t(`crm.workspace.stages.${from}`)}</span>
                    <ArrowRight />
                    <span>{t(`crm.workspace.stages.${to}`)}</span>
                  </span>
                )}
                {(activity.kind === 'deal_updated' ||
                  activity.kind === 'activity_edited') && (
                  <span
                    className={styles.activityTitle}
                    title={describeChanges(metadata)}
                  >
                    {describeChanges(metadata)}
                  </span>
                )}
                {activity.dueAt && (
                  <span className={styles.activityDue}>
                    <CalendarDays />
                    {/* A task is due on a day, no time. */}
                    {activity.kind === 'task'
                      ? date(activity.dueAt)
                      : shortDateTime(activity.dueAt)}
                    {activity.completedAt ? (
                      <span className={styles.successText}>
                        <Check />
                        {t('crm.workspace.completed')}
                      </span>
                    ) : (
                      activity.kind === 'task' && (
                        <CrmButton
                          variant="ghost"
                          disabled={busy === activity.id}
                          onClick={() => complete(activity)}
                        >
                          {t('crm.workspace.mark_done')}
                        </CrmButton>
                      )
                    )}
                  </span>
                )}
                {editable && (
                  <button
                    type="button"
                    className={styles.activityEdit}
                    aria-label={t('crm.workspace.edit_activity')}
                    title={t('crm.workspace.edit_activity')}
                    onClick={(event) => {
                      event.stopPropagation();
                      openEditor(activity);
                    }}
                  >
                    <Pencil aria-hidden="true" />
                  </button>
                )}
                <span className={styles.activityMeta}>
                  {actor}
                  {/* Inside a deal (no onDeal) the deal name is redundant. */}
                  {activity.deal && onDeal && (
                    <>
                      <span> · </span>
                      <button
                        type="button"
                        onClick={() => onDeal?.(activity.deal!.id)}
                      >
                        {activity.deal.name}
                      </button>
                    </>
                  )}
                  <span> · </span>
                  <time dateTime={activity.occurredAt}>
                    {shortDateTime(activity.occurredAt)}
                  </time>
                </span>
              </div>
              {activity.summary && (
                <p className={styles.emailSummaryLine}>
                  <Sparkles aria-hidden="true" />
                  {activity.summary}
                </p>
              )}
              {emailFrom && (
                <p className={styles.activitySub}>
                  {t('crm.workspace.email_from', { email: emailFrom })}
                </p>
              )}
              {!compact && activity.kind.includes('email') && (
                <>
                  {subject && subject !== activity.title && (
                    <p className={styles.activitySub}>{subject}</p>
                  )}
                  {recipients.length > 0 && (
                    <p className={styles.activitySub}>
                      {t('crm.workspace.email_to', {
                        emails: recipients.join(', '),
                      })}
                    </p>
                  )}
                </>
              )}
              {!compact && activity.body && (
                <button
                  type="button"
                  aria-expanded={open}
                  className={`${styles.activityBody} ${open ? styles.activityBodyOpen : ''}`}
                  onClick={() =>
                    setExpanded((old) => {
                      const next = new Set(old);
                      if (next.has(activity.id)) next.delete(activity.id);
                      else next.add(activity.id);
                      return next;
                    })
                  }
                >
                  {activity.body}
                </button>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
};
export const CrmActivities = ({
  search,
  onDeal,
}: {
  search: string;
  onDeal: (id: string) => void;
}) => {
  const { t } = useCrmFormat();
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState('all');
  const activities = useQuery({
    queryKey: ['crm', 'activities', page, kind, search],
    queryFn: () =>
      getCrmActivities({ page, search, ...(kind === 'all' ? {} : { kind }) }),
  });
  return (
    <>
      <div className={styles.filters}>
        <ComboBox
          size="sm"
          allowUnselect={false}
          value={kind}
          options={[
            'all',
            'note',
            'call',
            'meeting',
            'task',
            'email_sent',
            'email_received',
          ].map((value) => ({
            value,
            label: t(`crm.workspace.activityFilters.${value}`),
          }))}
          onChange={(value) => {
            setKind(value || 'all');
            setPage(1);
          }}
        />
        <span className={styles.muted}>
          {t('crm.workspace.activity_automatic')}
        </span>
      </div>
      <div className={styles.panel}>
        {activities.isPending ? (
          <p role="status" className={styles.loading}>
            {t('crm.loading')}
          </p>
        ) : activities.isError ? (
          <CrmError retry={() => void activities.refetch()} />
        ) : !activities.data.items.length ? (
          <CrmEmpty
            title={t('crm.workspace.no_activities')}
            description={t('crm.workspace.activities_empty')}
          />
        ) : (
          <CrmActivityFeed activities={activities.data.items} onDeal={onDeal} />
        )}
      </div>
      {activities.data && (
        <CrmPagination
          page={page}
          total={activities.data.total}
          pageSize={activities.data.pageSize}
          onPage={setPage}
        />
      )}
    </>
  );
};
